import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { formatIDR, formatAUD } from "@/lib/format";
import { PageHeader, Field, EmptyState } from "@/components/ui-helpers";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SearchableSelect } from "@/components/SearchableSelect";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Plus, Search, Package, X, Copy, MessageCircle, Printer, Save, Check, FileText, Trash2, Loader2, ClipboardList } from "lucide-react";
import { toast } from "sonner";
import { ScanSingle, ScanBulk } from "@/pages/OrderScan";

// --- Product picker dialog (self-contained; filters by trip) ---
function ProductPicker({ products, onPick }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const results = products.filter((p) => {
    if (!q) return true;
    const hay = [p.name, p.brand, p.category, p.variant, p.product_code].filter(Boolean).join(" ").toLowerCase();
    return hay.includes(q.toLowerCase());
  });
  return (
    <>
      <Button type="button" size="sm" onClick={() => { setOpen(true); setQ(""); }} data-testid="conf-add-product">
        <Search className="h-3.5 w-3.5 mr-1" />Cari Produk
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg p-0 gap-0 max-h-[80vh] flex flex-col">
          <DialogHeader className="p-4 pb-2"><DialogTitle>Pilih Produk</DialogTitle></DialogHeader>
          <div className="px-4 pb-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input autoFocus className="pl-9" placeholder="Cari nama / kode / brand…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="conf-product-search" />
            </div>
          </div>
          <div className="flex-1 overflow-y-auto px-4 pb-4 space-y-2" data-testid="conf-product-results">
            {results.length === 0 ? (
              <p className="text-center text-sm text-muted-foreground py-8">Produk tidak ditemukan untuk trip ini</p>
            ) : results.map((p) => (
              <button type="button" key={p.id} onClick={() => { onPick(p); setOpen(false); }} data-testid="conf-product-result"
                className="w-full flex items-center gap-3 p-2.5 rounded-xl border border-border bg-card hover:bg-secondary/50 text-left transition-colors">
                <div className="h-12 w-12 rounded-lg bg-secondary flex items-center justify-center overflow-hidden shrink-0">
                  {p.photo ? <img src={p.thumb_url || p.photo} alt={p.name} className="h-full w-full object-cover" /> : <Package className="h-5 w-5 text-muted-foreground" />}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{p.name}</p>
                  <p className="text-[11px] font-mono text-muted-foreground">{p.product_code}</p>
                  <div className="flex items-center gap-2 mt-0.5">
                    <span className="font-mono text-sm">{formatIDR(p.pricing?.final_price)}</span>
                    {p.discount_active && p.discount_percent > 0 && <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-md bg-[hsl(var(--danger-bg))] text-[hsl(var(--danger))]">-{p.discount_percent}%</span>}
                  </div>
                </div>
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default function OrderConfirmation() {
  const navigate = useNavigate();
  const { trips, selectedTripId } = useApp();
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [trip_id, setTripId] = useState(selectedTripId !== "all" ? selectedTripId : "");
  const [custMode, setCustMode] = useState("existing");
  const [customer_id, setCustomerId] = useState("");
  const [customer_name, setCustomerName] = useState("");
  const [customer_whatsapp, setCustomerWa] = useState("");
  const [items, setItems] = useState([]);
  const [notes, setNotes] = useState("");
  const [preview, setPreview] = useState(null);
  const [draftId, setDraftId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [drafts, setDrafts] = useState([]);
  const [tab, setTab] = useState("builder");
  const [confirmTarget, setConfirmTarget] = useState(null);

  const trip = trips.find((t) => t.id === trip_id);
  const tripProducts = products.filter((p) => p.trip_id === trip_id);

  useEffect(() => {
    if (!trip_id && trips.length) setTripId(trips[0].id);
  }, [trips]); // eslint-disable-line

  useEffect(() => {
    api.get("/customers").then((r) => setCustomers(Array.isArray(r.data) ? r.data : (r.data?.items || []))).catch(() => {});
    api.get("/products", { params: { page_size: 1000 } })
      .then((r) => setProducts(Array.isArray(r.data) ? r.data : (r.data?.items || []))).catch(() => {});
  }, []);

  const loadDrafts = useCallback(() => {
    api.get("/order-drafts").then((r) => setDrafts(r.data || [])).catch(() => {});
  }, []);
  useEffect(() => { loadDrafts(); }, [loadDrafts]);

  const addItem = (p) => {
    setItems((its) => {
      const idx = its.findIndex((it) => it.product_id === p.id && (it.variant || "") === (p.variant || ""));
      if (idx >= 0) return its.map((it, i) => i === idx ? { ...it, quantity: Number(it.quantity || 0) + 1 } : it);
      return [...its, {
        product_id: p.id, product_name: p.name, photo: p.thumb_url || p.photo || null,
        variant: p.variant || "", quantity: 1, aud_price: p.aud_price || "0",
        product_code: p.product_code || "",
        margin_percent: p.margin_percent || trip?.default_margin_percent || "",
        jasa_titip_type: p.jasa_titip_type || trip?.default_jasa_titip_type || "fixed",
        jasa_titip_value: p.jasa_titip_value || trip?.default_jasa_titip_value || "",
        delivery_fee: p.delivery_fee || trip?.default_delivery_fee || "",
        rounding_method: p.rounding_method || trip?.default_rounding_method || "none",
        additional_fee: "0", discount: "0",
        promo_active: !!p.discount_active, promo_type: p.discount_type || "percent",
        promo_percent: Number(p.discount_percent) || 0, promo_amount: Number(p.discount_amount) || 0,
      }];
    });
  };
  const updItem = (i, k, v) => setItems((its) => its.map((it, idx) => idx === i ? { ...it, [k]: v } : it));
  const rmItem = (i) => setItems((its) => its.filter((_, idx) => idx !== i));

  const custPayload = useCallback(() => (
    custMode === "existing" ? { customer_id } : { customer_name, customer_whatsapp }
  ), [custMode, customer_id, customer_name, customer_whatsapp]);

  // Debounced live preview
  const debTimer = useRef(null);
  useEffect(() => {
    if (!trip_id || items.length === 0) { setPreview(null); return; }
    if (debTimer.current) clearTimeout(debTimer.current);
    debTimer.current = setTimeout(async () => {
      try {
        const r = await api.post("/orders/preview", {
          trip_id, items, notes, ...custPayload(),
          reference: drafts.find((d) => d.id === draftId)?.reference || null,
        });
        setPreview(r.data);
      } catch { setPreview(null); }
    }, 350);
    return () => debTimer.current && clearTimeout(debTimer.current);
  }, [trip_id, items, notes, custMode, customer_id, customer_name, customer_whatsapp, draftId]); // eslint-disable-line

  const resetBuilder = () => {
    setItems([]); setNotes(""); setCustomerId(""); setCustomerName(""); setCustomerWa("");
    setDraftId(null); setPreview(null); setCustMode("existing");
  };

  const draftBody = () => ({ trip_id, items, notes, ...custPayload() });

  const saveDraft = async () => {
    if (!trip_id) return toast.error("Pilih trip");
    if (items.length === 0) return toast.error("Tambahkan minimal 1 produk");
    setBusy(true);
    try {
      let res;
      if (draftId) res = await api.put(`/order-drafts/${draftId}`, draftBody());
      else res = await api.post("/order-drafts", draftBody());
      setDraftId(res.data.id);
      setPreview(res.data.priced ? { ...res.data.priced, confirmation_text: res.data.confirmation_text, customer: res.data.priced?.customer } : preview);
      if (res.data.confirmation_text) setPreview((p) => ({ ...(p || {}), confirmation_text: res.data.confirmation_text }));
      toast.success(draftId ? "Draft diperbarui" : `Draft disimpan · ${res.data.reference}`);
      loadDrafts();
      return res.data;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gagal menyimpan draft");
    } finally { setBusy(false); }
  };

  const buildOrder = async () => {
    if (!trip_id) return toast.error("Pilih trip");
    if (items.length === 0) return toast.error("Tambahkan minimal 1 produk");
    if (custMode === "existing" && !customer_id) return toast.error("Pilih pelanggan");
    if (custMode === "new" && !customer_name.trim()) return toast.error("Isi nama pelanggan baru");
    setBusy(true);
    try {
      let id = draftId;
      if (!id) { const d = await api.post("/order-drafts", draftBody()); id = d.data.id; setDraftId(id); }
      else { await api.put(`/order-drafts/${id}`, draftBody()); }
      const r = await api.post(`/order-drafts/${id}/confirm`);
      toast.success(`Order dibuat · ${r.data.order.order_number}`);
      resetBuilder(); loadDrafts();
      navigate("/orders");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gagal membuat order");
    } finally { setBusy(false); }
  };

  const confText = preview?.confirmation_text || "";
  const copyText = async (text, label = "Teks konfirmasi disalin") => {
    try { await navigator.clipboard.writeText(text); toast.success(label); }
    catch { toast.error("Gagal menyalin"); }
  };
  const copyBlank = async () => {
    try { const r = await api.get("/orders/blank-template"); await copyText(r.data.template, "Template kosong disalin"); }
    catch { toast.error("Gagal mengambil template"); }
  };
  const waNumber = (preview?.customer?.whatsapp || customer_whatsapp || "").replace(/\D/g, "").replace(/^0/, "62");
  const shareWa = () => {
    if (!confText) return toast.error("Belum ada teks konfirmasi");
    const base = waNumber ? `https://wa.me/${waNumber}` : "https://wa.me/";
    window.open(`${base}?text=${encodeURIComponent(confText)}`, "_blank");
  };
  const printText = () => {
    if (!confText) return;
    const w = window.open("", "_blank", "width=420,height=640");
    if (!w) return;
    w.document.write(`<pre style="font-family:ui-monospace,monospace;font-size:13px;white-space:pre-wrap;padding:16px">${confText.replace(/</g, "&lt;")}</pre>`);
    w.document.close(); w.focus(); w.print();
  };

  const openDraft = (d) => {
    setTripId(d.trip_id || "");
    if (d.customer_id) { setCustMode("existing"); setCustomerId(d.customer_id); }
    else { setCustMode("new"); setCustomerName(d.customer_name || ""); setCustomerWa(d.customer_whatsapp || ""); }
    setItems(d.items || []); setNotes(d.notes || ""); setDraftId(d.id);
    setTab("builder");
    toast.success(`Draft ${d.reference} dimuat`);
  };
  const deleteDraft = async (d) => {
    try { await api.delete(`/order-drafts/${d.id}`); toast.success("Draft dihapus"); if (draftId === d.id) resetBuilder(); loadDrafts(); }
    catch { toast.error("Gagal menghapus draft"); }
  };
  const confirmDraft = async (d) => {
    setBusy(true);
    try { const r = await api.post(`/order-drafts/${d.id}/confirm`); toast.success(`Order dibuat · ${r.data.order.order_number}`); loadDrafts(); setConfirmTarget(null); }
    catch (e) { toast.error(e.response?.data?.detail || "Gagal membuat order"); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-6 pb-10" data-testid="order-confirmation-page">
      <PageHeader title="Konfirmasi Order" subtitle="Siapkan order, kirim konfirmasi ke pelanggan via WhatsApp, lalu buat order." >
        <Button variant="outline" onClick={copyBlank} data-testid="conf-copy-blank"><FileText className="h-4 w-4 mr-1.5" />Salin Template Kosong</Button>
        <Button variant="outline" onClick={() => navigate("/orders")}>Ke Daftar Order</Button>
      </PageHeader>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList data-testid="conf-tabs">
          <TabsTrigger value="builder" data-testid="conf-tab-builder">Buat Konfirmasi</TabsTrigger>
          <TabsTrigger value="scan" data-testid="conf-tab-scan">Scan / Tempel</TabsTrigger>
          <TabsTrigger value="bulk" data-testid="conf-tab-bulk">Bulk Scan</TabsTrigger>
          <TabsTrigger value="drafts" data-testid="conf-tab-drafts">Draft {drafts.filter((d) => d.status !== "CONFIRMED").length > 0 && `(${drafts.filter((d) => d.status !== "CONFIRMED").length})`}</TabsTrigger>
        </TabsList>

        <TabsContent value="builder" className="mt-4">
          <div className="grid lg:grid-cols-2 gap-6">
            {/* LEFT: builder */}
            <div className="space-y-4">
              <div className="rounded-xl border border-border bg-card p-4 space-y-4">
                <div className="grid sm:grid-cols-2 gap-4">
                  <Field label="Trip" required>
                    <SearchableSelect
                      value={trip_id}
                      onChange={(v) => { setTripId(v); }}
                      options={trips.map((t) => ({ value: t.id, label: t.name, keywords: [t.name, t.code] }))}
                      placeholder="Pilih trip"
                      searchPlaceholder="Cari trip..."
                      emptyText="Trip tidak ditemukan"
                      testId="conf-trip-select"
                    />
                  </Field>
                  <Field label="Pelanggan" required>
                    <div className="flex gap-1 mb-2">
                      <Button type="button" size="sm" variant={custMode === "existing" ? "default" : "outline"} className="h-8 flex-1" onClick={() => setCustMode("existing")} data-testid="conf-cust-existing">Pilih ada</Button>
                      <Button type="button" size="sm" variant={custMode === "new" ? "default" : "outline"} className="h-8 flex-1" onClick={() => setCustMode("new")} data-testid="conf-cust-new">Baru</Button>
                    </div>
                    {custMode === "existing" ? (
                      <SearchableSelect
                        value={customer_id}
                        onChange={setCustomerId}
                        options={customers.map((c) => ({ value: c.id, label: c.name, sublabel: [c.whatsapp, c.instagram].filter(Boolean).join(" · "), keywords: [c.name, c.whatsapp, c.phone, c.instagram, c.email] }))}
                        placeholder="Pilih pelanggan"
                        searchPlaceholder="Cari nama / WhatsApp / IG..."
                        emptyText="Pelanggan tidak ditemukan"
                        testId="conf-customer-select"
                      />
                    ) : (
                      <div className="space-y-2">
                        <Input placeholder="Nama pelanggan" value={customer_name} onChange={(e) => setCustomerName(e.target.value)} data-testid="conf-new-name" />
                        <Input placeholder="Nomor WhatsApp (08…)" value={customer_whatsapp} onChange={(e) => setCustomerWa(e.target.value)} data-testid="conf-new-wa" />
                      </div>
                    )}
                  </Field>
                </div>

                <div className="border border-border rounded-xl p-3 space-y-3">
                  <div className="flex items-center justify-between gap-2">
                    <p className="font-medium text-sm">Produk ({items.length})</p>
                    <ProductPicker products={tripProducts} onPick={addItem} />
                  </div>
                  {items.length === 0 && <p className="text-xs text-muted-foreground text-center py-3">Belum ada produk. Cari dari katalog trip ini.</p>}
                  {items.map((it, i) => (
                    <div key={`${it.product_id}-${i}`} className="rounded-lg border border-border p-3 space-y-2" data-testid="conf-item-row">
                      <div className="flex items-center gap-2">
                        <div className="h-9 w-9 rounded-lg bg-secondary flex items-center justify-center overflow-hidden shrink-0">
                          {it.photo ? <img src={it.photo} alt={it.product_name} className="h-full w-full object-cover" /> : <Package className="h-4 w-4 text-muted-foreground" />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">{it.product_name}</p>
                          <p className="text-[10px] font-mono text-muted-foreground">{it.product_code} · {formatAUD(it.aud_price)}{it.promo_active && it.promo_percent > 0 ? ` · -${it.promo_percent}%` : ""}</p>
                        </div>
                        <Button type="button" size="sm" variant="ghost" className="text-destructive" onClick={() => rmItem(i)} data-testid="conf-item-remove"><X className="h-4 w-4" /></Button>
                      </div>
                      <div className="flex items-center gap-2">
                        <label className="text-xs text-muted-foreground">Qty</label>
                        <Input type="number" min="1" className="h-8 w-24" value={it.quantity} onChange={(e) => updItem(i, "quantity", e.target.value)} data-testid="conf-item-qty" />
                      </div>
                    </div>
                  ))}
                </div>

                <Field label="Catatan"><Textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Catatan untuk pelanggan (opsional)" data-testid="conf-notes" /></Field>
              </div>
            </div>

            {/* RIGHT: preview + actions */}
            <div className="space-y-4">
              <div className="rounded-xl border border-border bg-card p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <p className="font-medium text-sm flex items-center gap-2"><ClipboardList className="h-4 w-4 text-primary" />Pratinjau Konfirmasi</p>
                  {draftId && <Badge variant="secondary" data-testid="conf-draft-ref">{drafts.find((d) => d.id === draftId)?.reference || "Draft"}</Badge>}
                </div>
                {confText ? (
                  <pre className="text-xs font-mono whitespace-pre-wrap bg-secondary/40 rounded-lg p-3 max-h-[320px] overflow-y-auto" data-testid="conf-preview-text">{confText}</pre>
                ) : (
                  <div className="text-sm text-muted-foreground py-10 text-center">Tambahkan produk untuk melihat pratinjau konfirmasi.</div>
                )}
                {preview && (
                  <div className="flex items-center justify-between text-sm border-t border-border pt-2">
                    <span className="text-muted-foreground">Total</span>
                    <span className="font-heading font-semibold text-primary" data-testid="conf-grand-total">{formatIDR(preview.grand_total)}</span>
                  </div>
                )}
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                <Button variant="outline" onClick={() => copyText(confText)} disabled={!confText} data-testid="conf-copy"><Copy className="h-4 w-4 mr-1.5" />Salin</Button>
                <Button variant="outline" onClick={shareWa} disabled={!confText} data-testid="conf-wa"><MessageCircle className="h-4 w-4 mr-1.5" />WhatsApp</Button>
                <Button variant="outline" onClick={printText} disabled={!confText} data-testid="conf-print"><Printer className="h-4 w-4 mr-1.5" />Cetak</Button>
                <Button variant="outline" onClick={saveDraft} disabled={busy || items.length === 0} data-testid="conf-save-draft">{busy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Save className="h-4 w-4 mr-1.5" />}Simpan Draft</Button>
                <Button className="col-span-2 sm:col-span-1" onClick={buildOrder} disabled={busy || items.length === 0} data-testid="conf-create-order">{busy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Check className="h-4 w-4 mr-1.5" />}Buat Order</Button>
              </div>
              {draftId && <Button variant="ghost" size="sm" className="w-full text-muted-foreground" onClick={resetBuilder} data-testid="conf-new-blank">+ Konfirmasi baru (kosongkan)</Button>}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="scan" className="mt-4">
          <ScanSingle trips={trips} trip_id={trip_id} setTripId={setTripId} tripProducts={tripProducts} customers={customers} onCreated={loadDrafts} />
        </TabsContent>

        <TabsContent value="bulk" className="mt-4">
          <ScanBulk trips={trips} trip_id={trip_id} setTripId={setTripId} tripProducts={tripProducts} customers={customers} onCreated={loadDrafts} />
        </TabsContent>

        <TabsContent value="drafts" className="mt-4">
          {drafts.length === 0 ? (
            <EmptyState title="Belum ada draft" subtitle="Draft konfirmasi yang Anda simpan akan muncul di sini." />
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {drafts.map((d) => (
                <div key={d.id} className="rounded-xl border border-border bg-card p-4 space-y-2" data-testid="conf-draft-card">
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-mono text-xs text-muted-foreground">{d.reference}</span>
                    <Badge variant={d.status === "CONFIRMED" ? "default" : "secondary"}>{d.status === "CONFIRMED" ? "Sudah jadi order" : "Draft"}</Badge>
                  </div>
                  <p className="font-medium text-sm">{d.customer_display?.name || "(tanpa nama)"}</p>
                  <p className="text-xs text-muted-foreground">{d.item_count} produk · {d.grand_total != null ? formatIDR(d.grand_total) : "-"}</p>
                  <div className="flex items-center gap-1.5 pt-1">
                    {d.status !== "CONFIRMED" ? (
                      <>
                        <Button size="sm" variant="outline" className="flex-1 h-8" onClick={() => openDraft(d)} data-testid="conf-draft-open">Buka</Button>
                        <Button size="sm" className="h-8" onClick={() => setConfirmTarget(d)} data-testid="conf-draft-confirm"><Check className="h-3.5 w-3.5 mr-1" />Jadikan Order</Button>
                        <Button size="sm" variant="outline" className="h-8 text-destructive" onClick={() => deleteDraft(d)} data-testid="conf-draft-delete"><Trash2 className="h-3.5 w-3.5" /></Button>
                      </>
                    ) : (
                      <Button size="sm" variant="outline" className="flex-1 h-8" onClick={() => navigate("/orders")}>Lihat di Order</Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      <Dialog open={!!confirmTarget} onOpenChange={(o) => !o && setConfirmTarget(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Jadikan Order?</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">Draft <span className="font-mono">{confirmTarget?.reference}</span> untuk {confirmTarget?.customer_display?.name || "pelanggan"} akan dibuat menjadi order resmi.</p>
          <div className="flex justify-end gap-2 mt-2">
            <Button variant="outline" onClick={() => setConfirmTarget(null)}>Batal</Button>
            <Button onClick={() => confirmDraft(confirmTarget)} disabled={busy} data-testid="conf-draft-confirm-yes">{busy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Check className="h-4 w-4 mr-1.5" />}Buat Order</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
